// ══════════════════════════════════════════════════════════════════════════
//  GRANULAR POSITION MANAGER — LIVE AUTO TRADING risk console (UI-P1)
//  God directive 2026-07-15 (RISK_LIMITS_3TIER_BLUEPRINT.md §9):
//  bot-wise + symbol-wise live P&L / status table, har symbol par surgical
//  EXIT (open legs flat) / STOP (L0 symbol-kill) / EXIT+STOP combo.
//  Data = canonical ledger (/api/trades/ledger) + /risk/symbol-controls —
//  wahi endpoints jo Detailed Trade Report / SymbolControlStrip use karte
//  hain, is liye numbers construction se reconcile hote hain.
//  EXITS SACRED: buttons sirf ENTRIES/positions par act karte hain — EXIT
//  hamesha allowed, STOP sirf nayi entries rokta hai (server enforce karta
//  hai; yahan sirf triggers hain).
// ══════════════════════════════════════════════════════════════════════════
import { inrShort, pctTxt, pctCol } from '../liveTrading/fundUsage';
import React, { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense } from 'react';
import { API_BASE as API_BASE_URL } from '../lib/api';
import { subscribeLTP } from '../lib/marketFeed';
import {
  fillsOf, wavg, pxFmt, contractKey, contractLabel, exitContractMatches,
} from '../tradeAnalytics';
// God Phase 3 (2026-09-24): per-leg asset label, live CONTRACT price, premium /
// underlying levels and running M2M — one arithmetic module for every panel.
import { contractParts, levelsOf, runningM2M, type PnlRow } from '../liveTrading/pnlMath';
import { useContractLtps, ltpTargetOf, noLtpReason } from '../liveTrading/useContractLtps';
import { chartTargetForRow, chartTargetForCell, chartTargetForPinpoint, type ChartTarget } from '../liveTrading/chartTarget';
// The CHART quick action mounts a real SuperChart pane; lazy so this table
// costs nothing until the first click.
const ContractChartDrawer = lazy(() => import('../liveTrading/ContractChartDrawer'));
const SRC_COL: Record<string, string> = { ws: '#10b981', chain: '#60a5fa', bridge: '#a78bfa' };

// X-Admin-Key pattern (401 lesson 2026-07-09): cross-site cookies frontend→api
// par flow NahI karte — admin key header hi bharosemand hai; server extra
// headers ignore karta hai isliye non-admin routes par bhi harmless.
const adminHeaders = (): Record<string, string> => {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const k = localStorage.getItem('jaiswal_admin_key');
    if (k) h['X-Admin-Key'] = k;
  } catch { /* SSR/private mode */ }
  return h;
};

// dir: +1 = long / BUY-to-open, −1 = SHORT / SELL-to-open (futures can be either;
// options are BUY so dir=+1). Needed so RUNNING P&L has the right sign per leg.
type OpenLeg = { id: string; optionSymbol: string; expiry?: string; entryPrice: number; openQty: number; dir: number;
  /** the leg's own timeframe and its raw ledger row (asset parts, levels, feed key) */
  tf: string; row: PnlRow };
// God 2026-08-24: a symbol row is several legs, one per timeframe, and rolling
// them into one figure hides which leg is losing. The Cell stays keyed by
// bot|symbol on purpose — STOP is /risk/symbol-kill, a symbol-scoped control,
// so a per-TF STOP button would promise something the backend cannot do. The TF
// split rides INSIDE the cell: its P&L and its own legs are real per TF, and
// EXIT is per-tradeId so a per-TF EXIT is genuine.
/** One contract's day inside a (symbol × TF) leg. This is the level that answers
 *  the question a day-realised number cannot: WHICH option did this trade, and
 *  did the sell land on the same contract the buy opened. `mismatched` counts
 *  rows whose exit named a different contract than their entry — provable only
 *  for rows written after the ledger began stamping `exitSecurityId`. */
type ContractStat = {
  label: string; trades: number;
  buyQty: number; buySum: number; sellQty: number; sellSum: number;
  realized: number; openQty: number; mismatched: number;
  /** every trade of this contract, oldest first - the entry/exit time stamps God asked for (2026-09-29) */
  rows: any[];
};
type TfSlice = {
  dayRealized: number; openQty: number; openLegs: OpenLeg[];
  trades: number; buyQty: number; buySum: number; sellQty: number; sellSum: number;
  /** fund used = largest capitalUsed of one trade on this leg (one position at a time); wins / closed for win % (God 2026-10-06) */
  fund: number; wins: number; closed: number;
  contracts: Record<string, ContractStat>;
};
type Cell = {
  bot: string; botName: string; symbol: string;
  dayRealized: number; openQty: number; openLegs: OpenLeg[];
  lastClosed: 'WIN' | 'LOSS' | 'BREAKEVEN' | null; lastExitTime: string;
  voidedCount: number;
  byTf: Record<string, TfSlice>;
};
const TF_ORDER = ['1s', '5s', '15s', '30s', '1m', '3m', '5m', '15m', '30m', '1h', '1H', '4h', '4H', '1d', '1D', '1W'];
const tfRank = (tf: string) => { const i = TF_ORDER.indexOf(String(tf)); return i < 0 ? 999 : i; };

const istToday = () => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
const fmt = (n: number) => `${n < 0 ? '−' : '+'}₹${Math.abs(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

// FUND USAGE (God 2026-10-06 ~19:12): fund used, win % and return % per leg / symbol / bot - same
// definitions as the Funds & Margin "Fund Usage & Return" tab (liveTrading/fundUsage.ts).
type U = { fund: number; wins: number; closed: number };
const sliceUsage = (sl: TfSlice): U => ({ fund: sl.fund || 0, wins: sl.wins || 0, closed: sl.closed || 0 });
const sumUsage = (us: U[]): U => us.reduce((a, u) => ({ fund: a.fund + u.fund, wins: a.wins + u.wins, closed: a.closed + u.closed }), { fund: 0, wins: 0, closed: 0 });
const cellUsage = (c: Cell): U => sumUsage(Object.values(c.byTf).map(sliceUsage));
const usageLine = (u: U, pnl: number) => (
  <div style={{ fontSize: 8, fontWeight: 700, marginTop: 1, whiteSpace: 'nowrap' }}
       title="Fund used = sabse bada ek-trade capital (har TF leg ka jod) · Win % = WIN ÷ closed · Return % = realized (all-incl) ÷ fund">
    <span style={{ color: '#818cf8' }}>{u.fund ? inrShort(u.fund) : '—'}</span>
    <span style={{ color: '#fbbf24', marginLeft: 5 }}>{u.closed ? `${(100 * u.wins / u.closed).toFixed(0)}%W` : ''}</span>
    <span style={{ color: pctCol(u.fund ? 100 * pnl / u.fund : null), marginLeft: 5 }}>{u.fund ? pctTxt(100 * pnl / u.fund) : ''}</span>
  </div>
);

const S = {
  wrap: { padding: '8px 20px', background: '#0b0f1a', borderBottom: '1px solid #1a1f3a' } as React.CSSProperties,
  head: { fontSize: 8, fontWeight: 800, color: '#64748b', letterSpacing: 1.5 } as React.CSSProperties,
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 10, marginTop: 6 },
  th: { textAlign: 'left' as const, padding: '3px 8px', fontSize: 8, fontWeight: 800, color: '#64748b', borderBottom: '1px solid #1a1f3a', letterSpacing: 1 },
  td: { padding: '4px 8px', borderBottom: '1px solid #131a2e', color: '#cbd5e1' },
  btn: (color: string, bg: string, border: string): React.CSSProperties => ({
    fontSize: 8, fontWeight: 900, padding: '2px 8px', borderRadius: 4, cursor: 'pointer',
    border: `1px solid ${border}`, background: bg, color, marginRight: 4,
  }),
};

const STATUS_META: Record<string, { icon: string; color: string }> = {
  KILLED:   { icon: '🔴', color: '#f87171' },
  RUNNING:  { icon: '🟢', color: '#34d399' },
  'SL HIT': { icon: '🟡', color: '#fbbf24' },
  BOOKED:   { icon: '✅', color: '#34d399' },
  IDLE:     { icon: '⚪', color: '#64748b' },
};

// variant: 'strip' = slim clickable summary bar (top placement);
//          'panel' = DEPLOYED STRATEGIES ke ⚡ POSITIONS tab ke andar inline
//          full table (God 2026-07-15 — "yahi best jagah hai"), FULL SCREEN
//          button dono me modal kholta hai.
/**
 * lane — which trading lane this panel belongs to (God 2026-08-26).
 *
 * Like FleetRegistryCards, this fetches its OWN ledger and deployments instead
 * of receiving a lane-filtered array, so upstream filtering never reached it
 * and the KALI page kept showing PAPER positions. Defaults to 'paper' — a panel
 * that was not told its lane must never be the real-money one.
 */
export const GranularPositionManager: React.FC<{ variant?: 'strip' | 'panel'; lane?: 'paper' | 'kali' }> =
  ({ variant = 'strip', lane = 'paper' }) => {
  // Absent tradingMode counts as PAPER, matching the LiveTrading lane filter.
  const LANE_MODE = lane === 'kali' ? 'LIVE' : 'PAPER';
  const inLane = (m: unknown) =>
    (String(m || 'PAPER').toUpperCase() === 'LIVE' ? 'LIVE' : 'PAPER') === LANE_MODE;

  const [cells, setCells] = useState<Cell[]>([]);
  const [kills, setKills] = useState<string[]>([]);
  const [botCaps, setBotCaps] = useState<Record<string, { name: string; maxLoss: number }>>({});
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState('');
  // Live RUNNING (unrealized) P&L per open leg (keyed by leg id) — polls the
  // option premium via /options/chain (the publicly-proxied endpoint) so the
  // DEPLOYED STRATEGIES table shows a live green/red P&L for RUNNING positions.
  const [legPnl, setLegPnl] = useState<Record<string, { ltp: number; pnl: number }>>({});
  // Last time the positions SSOT was pulled, so WS ticks can trigger a refresh
  // without a fast-ticking symbol turning into a request storm. A ref, not
  // state: bumping it must never itself cause a render.
  const lastPullRef = useRef(0);
  // Which (bot|symbol) rows have their TIMEFRAME rows open, and which
  // (bot|symbol|TF) rows have their CONTRACT breakdown open. Both collapsed by
  // default: rendering every TF of every symbol at once turned a 9-bot table
  // into a page you had to scroll to read one number. Depth is available on
  // click, and costs nothing when it is not.
  const [openSym, setOpenSym] = useState<Set<string>>(() => new Set());
  const [openTf, setOpenTf] = useState<Set<string>>(() => new Set());
  // DEPTH/SIGNALS-style UX (God 2026-07-15): default = slim strip; click →
  // full-detail MODAL; modal me ⛶ full-screen toggle; Esc = close.
  const [open, setOpen] = useState(false);
  const [fs, setFs] = useState(false);
  // CHART quick action (God Phase 3 item 12): which contract/TF the drawer shows.
  const [chartTarget, setChartTarget] = useState<ChartTarget | null>(null);
  const chartRef = useRef<ChartTarget | null>(null);
  chartRef.current = chartTarget;
  useEffect(() => {
    // Esc closes the drawer first (it has its own handler); the modal only
    // closes on a second Esc, so a chart never takes the whole table with it.
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') { if (chartRef.current) return; setOpen(false); setFs(false); } };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(''), 5000); };

  const load = useCallback(async () => {
    try {
      const [lj, kc, dj] = await Promise.all([
        // God 2026-09-02: this asked for the ENTIRE ledger and then threw away
        // everything except today in the browser. Measured live mid-session:
        // 2,956,715 bytes in 13.93s, against 86,553 bytes in 0.72s once the
        // window is passed — 34x smaller, 19x faster, same 54 rows and same 13
        // OPEN positions. The response was slower than the caller's own timeout,
        // so the fetch aborted, `.catch(() => null)` fired, rows became [] and
        // the table rendered "aaj koi trade/position nahi" while the sidebar
        // showed 13 live positions. Same 13.9s response is what tripped
        // LiveTrading's 8s summary timeout twice and printed "Sync dropped".
        // The endpoint has supported from/mode/openAlways/limit all along, and
        // FleetRegistryCards + LiveTrading already call it that way; this was
        // the one caller left unbounded, and it got slower as the day filled up.
        // openAlways=1 is NOT optional: Shool holds positional futures for days,
        // and a date window without it would drop an OPEN position off the book.
        fetch(`${API_BASE_URL}/api/trades/ledger?from=${istToday()}&mode=${LANE_MODE}`
              + `&openAlways=1&limit=2000&t=${Date.now()}`,
              { cache: 'no-store', signal: AbortSignal.timeout(12000) })
          .then(r => r.json()).catch(() => null),
        fetch(`${API_BASE_URL}/risk/symbol-controls?t=${Date.now()}`, { cache: 'no-store' }).then(r => r.json()).catch(() => null),
        fetch(`${API_BASE_URL}/deployments?t=${Date.now()}`, { cache: 'no-store' }).then(r => r.json()).catch(() => null),
      ]);
      if (kc?.status === 'success') setKills((kc.controls || []).filter((k: any) => k.killed).map((k: any) => k.symbol));
      const deps: any[] = Array.isArray(dj) ? dj : (dj?.deployments || dj?.data || []);
      const caps: Record<string, { name: string; maxLoss: number }> = {};
      deps.filter((d: any) => inLane(d?.tradingMode)).forEach((d: any) => { const id = d.id || d.botId; if (id) caps[id] = { name: d.name || id, maxLoss: Number(d.maxLossPerDay) || 5000 }; });
      if (Object.keys(caps).length) setBotCaps(caps);

      const rows: any[] = Array.isArray(lj?.trades) ? lj.trades : [];
      const today = istToday();
      const map = new Map<string, Cell>();
      // An OPEN row must survive the date test. openAlways=1 deliberately returns
      // positions opened BEFORE today (Shool carries futures for days), and a bare
      // `dateIso === today` would delete exactly those from the book — the server
      // route carries that same warning in its own comment. Realised P&L still
      // only counts today, because dayRealized is summed from closed rows below.
      rows.filter(t => ((t.dateIso || '') === today
                        || t.status === 'OPEN' || t.status === 'PARTIALLY_CLOSED')
                       && inLane(t?.tradingMode)).forEach(t => {
        const bot = t.botId || 'unknown';
        const sym = t.symbol || '—';
        const key = `${bot}|${sym}`;
        const c = map.get(key) || {
          bot, botName: t.botName || bot, symbol: sym, dayRealized: 0,
          openQty: 0, openLegs: [], lastClosed: null, lastExitTime: '', voidedCount: 0,
          byTf: {},
        };
        // A trade with no stamped TF lands in "(no TF)" rather than being folded
        // into a real leg — it is unattributable, and that is worth seeing.
        const tf = String(t.timeframe || '').trim() || '(no TF)';
        const slice: TfSlice = (c.byTf[tf] ||= {
          dayRealized: 0, openQty: 0, openLegs: [],
          trades: 0, buyQty: 0, buySum: 0, sellQty: 0, sellSum: 0, contracts: {},
          fund: 0, wins: 0, closed: 0,
        });
        if (t.voided) c.voidedCount += 1;
        if (['WIN', 'LOSS', 'BREAKEVEN'].includes(t.status)) {
          const _p = Number(t.netPnl ?? t.pnl) || 0;
          c.dayRealized += _p;
          slice.dayRealized += _p;
          const xt = t.exitTime || t.time || '';
          if (xt >= c.lastExitTime) { c.lastExitTime = xt; c.lastClosed = t.status; }
        }
        const oq = Number(t.openQty) || 0;
        // STRICT OPEN-LEG ISOLATION (God 2026-08-03): leg SIRF tab jab trade
        // GENUINELY open ho. Closed (WIN/LOSS/BREAKEVEN) ya VOIDED rows me
        // stale openQty residue reh sakta hai — pehle wo PHANTOM leg banata
        // tha aur RUNNING P&L me poore din ki closed trades mix ho jaati thin
        // (UI par -₹15k jaisa loss jabki risk-engine ke paas position hi
        // nahi — BHAIRAVI/kill-limit ki asliyat se out-of-sync). Ab RUNNING
        // P&L = sirf ACTIVE leg ka (LTP − usi leg ki entry) × usi leg ki qty.
        const genuinelyOpen = ['OPEN', 'PARTIALLY_CLOSED']
          .includes(String(t.status || '').toUpperCase()) && !t.voided;
        if (oq > 0 && genuinelyOpen) {
          // SHORT / SELL-to-open ⇒ −1 (short futures profit when price falls).
          const dir = String(t.direction || '').toUpperCase() === 'SHORT'
            || String(t.entrySide || t.side || '').toUpperCase() === 'SELL' ? -1 : 1;
          const leg: OpenLeg = { id: t.id, optionSymbol: t.optionSymbol || sym, expiry: (t as any).expiry || (t as any).expiryDate,
                                 entryPrice: Number(t.entryPrice) || 0, openQty: oq, dir, tf, row: t as PnlRow };
          c.openQty += oq;
          c.openLegs.push(leg);
          slice.openQty += oq;
          slice.openLegs.push(leg);   // same object — per-TF EXIT uses the same tradeIds
        }

        // ── per-TF fills, split by CONTRACT (God 2026-08-26) ────────────────
        // A voided row is a quarantined trade and contributes nothing here, the
        // same rule the realised P&L above already follows. Averages are
        // quantity-weighted sums now, divided only at render.
        if (!t.voided) {
          slice.trades += 1;
          slice.fund = Math.max(slice.fund, Number(t.capitalUsed) || 0);
          if (['WIN', 'LOSS', 'BREAKEVEN'].includes(t.status)) { slice.closed += 1; if (t.status === 'WIN') slice.wins += 1; }
          const ck = contractKey(t);
          const cs: ContractStat = (slice.contracts[ck] ||= {
            label: contractLabel(t), trades: 0,
            buyQty: 0, buySum: 0, sellQty: 0, sellSum: 0,
            realized: 0, openQty: 0, mismatched: 0, rows: [],
          });
          cs.trades += 1;
          cs.rows.push(t);
          if (['WIN', 'LOSS', 'BREAKEVEN'].includes(t.status)) cs.realized += Number(t.netPnl ?? t.pnl) || 0;
          if (exitContractMatches(t) === false) cs.mismatched += 1;
          if (oq > 0 && genuinelyOpen) cs.openQty += oq;
          for (const f of fillsOf(t)) {
            if (f.side === 'BUY') {
              slice.buySum += f.price * f.qty; slice.buyQty += f.qty;
              cs.buySum += f.price * f.qty; cs.buyQty += f.qty;
            } else {
              slice.sellSum += f.price * f.qty; slice.sellQty += f.qty;
              cs.sellSum += f.price * f.qty; cs.sellQty += f.qty;
            }
          }
        }
        map.set(key, c);
      });
      setCells(Array.from(map.values()));
    } catch { /* poll — agla cycle pakad lega */ }
  }, []);

  useEffect(() => { void load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);

  // ── LIVE CONTRACT PRICES (God Phase 3, 2026-09-24) ──────────────────────
  // Each open leg is priced on ITS OWN contract (option premium / futures /
  // stock — never the index spot): shared /ws/live-feed push first, the
  // per-underlying /options/chain server cache second, the bridge's own leg
  // price last. RULE #22: every lane reads the server cache, none the broker.
  const openLegsAll = useMemo(() => cells.flatMap(c => c.openLegs), [cells]);
  const ltpTargets = useMemo(() => openLegsAll.map(l => ltpTargetOf(l.row)), [openLegsAll]);
  const liveLtps = useContractLtps(ltpTargets, legPnl);

  // RUNNING P&L poll: resolve each open leg's live price and mark it to entry.
  //   • OPTION legs  → /options/chain (one call per underlying), CE/PE by strike.
  //   • FUTURES/CASH → /quote (one call per underlying), spot/fut LTP.
  // pnl = (ltp − entry) × openQty × dir  (dir = −1 for SHORT / SELL-to-open).
  // Shool trades FUTURES: without the /quote branch every RUNNING row was stuck
  // on "…" forever — the options-chain lookup can't price a FUT/CASH leg (no
  // strike/CE/PE). God 2026-07-23 fix.
  const legSig = cells.flatMap(c => c.openLegs.map(l => `${l.id}:${c.symbol}:${l.dir}`)).sort().join('|');
  useEffect(() => {
    const legs = cells.flatMap(c => c.openLegs);
    if (!legs.length) { setLegPnl({}); return; }
    let alive = true;
    // SINGLE SOURCE OF TRUTH (God 2026-08-03): saara client-side P&L math
    // REMOVED. Running P&L + LTP ab SIRF bridge /rag/positions se aate hain —
    // ledger-truth via BHUVANESHWARI gateway, EK canonical server-side
    // computation (wahi jo risk-console dekhta hai). Bridge unreachable =>
    // rows honest "…" dikhati hain — UI kabhi khud compute NAHI karta.
    const pull = async () => {
      try {
        // AWS migration (God 2026-08-03): positions SSOT now served from EC2 bridge.
        const r = await fetch(`https://api.jaiswalcapital.com/bridge/rag/positions?t=${Date.now()}`,
          { cache: 'no-store', signal: AbortSignal.timeout(6000) });
        const d = await r.json();
        if (!alive) return;
        if (r.ok && d?.ok) {
          const next: Record<string, { ltp: number; pnl: number }> = {};
          for (const l of (d.legs || [])) {
            if (l?.id && l.pnl != null && l.ltp != null) next[l.id] = { ltp: l.ltp, pnl: l.pnl };
          }
          // Only publish when a number actually MOVED. Previously every pull
          // handed React a brand-new object, so the whole grid re-rendered even
          // when the response was byte-identical to the last one. That is the
          // common case, not the rare one: the broker issues a new price per
          // instrument every ~4-10s (RULE #22) while this now refreshes on every
          // tick, so most refreshes legitimately carry no change. Bailing out
          // here is what makes "repaint only the cells that changed" true —
          // React cannot skip a render it was handed a new reference for.
          setLegPnl(prev => {
            const pk = Object.keys(prev), nk = Object.keys(next);
            if (pk.length === nk.length
                && nk.every(k => prev[k] && prev[k].pnl === next[k].pnl
                                          && prev[k].ltp === next[k].ltp)) {
              return prev;                     // identical — keep the reference
            }
            return next;
          });
        } else setLegPnl({});
      } catch { if (alive) setLegPnl({}); }
    };
    void pull();

    // ── TICK-DRIVEN REFRESH (God 2026-09-02) ────────────────────────────────
    // The grid used to repaint on a blind 5s timer, so a price that moved right
    // after a poll sat stale for nearly five seconds. Now every symbol we
    // actually hold is subscribed to the shared /ws/live-feed lane (ONE socket
    // for the whole app, spine mirror, measured p50 19.6ms) and a tick pulls
    // the numbers immediately.
    //
    // WHY THE BROWSER STILL DOES NOT COMPUTE P&L: the WS carries LTP, not P&L,
    // and God's 2026-08-03 order removed all client-side P&L math precisely so
    // this grid and the risk console can never disagree. Deriving pnl here from
    // a WS tick would repaint faster and quietly reintroduce two sources of
    // truth. So the tick is a TRIGGER, not a data source — the server stays the
    // only place P&L is computed.
    //
    // ZERO BACKEND SPAM: a 1s floor between refreshes. MCX ticks arrive far
    // faster than the broker changes a price (RULE #22: Dhan issues a new price
    // per instrument every ~4-10s), so without this floor a busy symbol would
    // hammer the bridge for numbers that had not moved. The 5s interval stays
    // as a backstop for the case where the socket is down and no tick ever
    // arrives — a quiet feed must not freeze the book.
    const syms = Array.from(new Set(cells.filter(c => c.openLegs.length).map(c => c.symbol)));
    const unsubs = syms.map(s => subscribeLTP(s, () => {
      const now = Date.now();
      if (now - lastPullRef.current < 1000) return;   // floor
      lastPullRef.current = now;
      void pull();
    }));

    const iv = setInterval(pull, 5000);
    return () => {
      alive = false;
      clearInterval(iv);
      unsubs.forEach(u => { try { u(); } catch { /* already torn down */ } });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legSig]);

  // per-cell running (unrealized) P&L from its open legs. The live CONTRACT
  // tick marks the leg through pnlMath.runningM2M — the one formula every panel
  // uses (God Phase 3, 2026-09-24) — and the bridge's own figure is the
  // fallback for a leg no lane can price, so the column is never blank while
  // the server still knows a number.
  const legM2M = (l: OpenLeg): { pnl: number; src: string } | null => {
    const lv = liveLtps[l.id];
    const m = lv ? runningM2M(l.row, lv.ltp) : null;
    if (m != null && lv) return { pnl: m, src: lv.source };
    const e = legPnl[l.id];
    return e ? { pnl: e.pnl, src: 'bridge-pnl' } : null;
  };
  const runPnlOfLegs = (legs: OpenLeg[]): { pnl: number; has: boolean } => {
    let pnl = 0, has = false;
    for (const l of legs) { const m = legM2M(l); if (m) { pnl += m.pnl; has = true; } }
    return { pnl, has };
  };
  // One open leg, fully described: ASSET (underlying · strike CE/PE · expiry)
  // · avg entry · live LTP (+source) · SL (premium `p` / underlying `u`) ·
  // TGT (underlying) · M2M · 📈 chart. Each price is labelled with its
  // currency; "—" means the ledger did not stamp it, never 0.
  const legLine = (l: OpenLeg): React.ReactNode => {
    const p = contractParts(l.row);
    const lv = levelsOf(l.row);
    const live = liveLtps[l.id];
    const m = legM2M(l);
    return (
      <span key={l.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginRight: 8, whiteSpace: 'nowrap' }}>
        <span style={{ color: '#cbd5e1' }} title={`${l.id} · ${l.optionSymbol}${l.tf ? ` · ${l.tf}` : ''}`}>{p.label}</span>
        <span style={{ color: '#60a5fa', fontFamily: 'monospace' }} title={l.dir === -1 ? 'average SELL price (written entry)' : 'average BUY price (entry)'}>@{pxFmt(lv.entry || null)}</span>
        <span style={{ fontFamily: 'monospace', color: live ? '#e2e8f0' : '#475569' }}
              title={live ? `live LTP of this contract · source ${live.source}` : noLtpReason(p.kind, !!p.strike)}>
          → {live ? `₹${pxFmt(live.ltp)}` : '—'}{live && <span style={{ fontSize: 7, color: SRC_COL[live.source] || '#94a3b8' }}> {live.source}</span>}
        </span>
        {lv.slPremium != null
          ? <span style={{ color: '#f87171' }} title={`stop-loss in option PREMIUM (dnaSlPremium · ${lv.slPremiumBasis || 'basis not stamped'})${lv.slUnderlying != null ? ` · underlying ${pxFmt(lv.slUnderlying)}` : ''}`}>SL {pxFmt(lv.slPremium)}<span style={{ fontSize: 7 }}>p</span></span>
          : lv.slUnderlying != null
            ? <span style={{ color: '#f87171' }} title="stop-loss on the UNDERLYING (dnaSl) — no premium stop stamped on this row">SL {pxFmt(lv.slUnderlying)}<span style={{ fontSize: 7 }}>u</span></span>
            : <span style={{ color: '#475569' }} title="no stop stamped on this row (dnaSl / dnaSlPremium empty)">SL —</span>}
        {lv.targetUnderlying != null
          ? <span style={{ color: '#34d399' }} title="profit-booking target on the UNDERLYING (dnaTarget) — the ledger stamps no premium target">TGT {pxFmt(lv.targetUnderlying)}<span style={{ fontSize: 7 }}>u</span></span>
          : <span style={{ color: '#475569' }} title="no target stamped on this row (dnaTarget empty)">TGT —</span>}
        <span style={{ fontWeight: 800, fontFamily: 'monospace', color: !m ? '#64748b' : m.pnl >= 0 ? '#34d399' : '#f87171' }}
              title={m ? `running M2M = (live − entry) × ${l.openQty}${l.dir === -1 ? ' × −1' : ''} · via ${m.src}` : 'no live price for this contract yet'}>
          {m ? fmt(m.pnl) : '…'}
        </span>
        <button onClick={(e) => { e.stopPropagation(); setChartTarget(chartTargetForRow(l.row, String(l.row.botId || ''))); }}
                title={`live chart · ${p.label} · ${l.tf || 'TF ?'}`}
                style={{ ...S.btn('#a5b4fc', 'rgba(99,102,241,.12)', 'rgba(99,102,241,.45)'), padding: '0 5px', marginRight: 0 }}>📈</button>
      </span>
    );
  };
  // Chart for a (bot × symbol [× TF]) row: the open contract when there is one,
  // else the instrument that lane trades (Shool → futures, others → underlying).
  const chartForCell = (c: Cell, tf?: string) => {
    // God 2026-09-29: a cell / TF chart opens on the UNDERLYING (the Pine's own axis, every fill of the cell and
    // the Pine's signals); the open contract is the drawer's CONTRACT toggle instead of the only view.
    const legs = tf ? (c.byTf[tf]?.openLegs || []) : c.openLegs;
    const t = tf || legs[0]?.tf || Object.keys(c.byTf).sort((a, b) => tfRank(a) - tfRank(b)).find(x => x !== '(no TF)') || '';
    const base = chartTargetForCell(c.bot, c.symbol, t);
    if (legs[0]) return { ...base, alt: chartTargetForRow(legs[0].row, c.bot) };
    // God 2026-10-14: an option lane always opens its option chart beside the index - with nothing open, the
    // contract of the cell's latest trade today (entry / SL / booking are judged on that premium chart)
    const slices = t ? [c.byTf[t]].filter(Boolean) : Object.values(c.byTf);
    const last = slices.flatMap(s => Object.values(s.contracts || {}).flatMap(k => k.rows || []))
      .sort((a, b) => String(b.time || '').localeCompare(String(a.time || '')))[0];
    return last ? { ...base, alt: chartTargetForRow(last, c.bot) } : base;
  };
  const runPnlOf = (c: Cell) => runPnlOfLegs(c.openLegs);

  const post = async (url: string, body: any): Promise<any> => {
    const r = await fetch(`${API_BASE_URL}${url}`, {
      method: 'POST', credentials: 'include',
      headers: adminHeaders(), body: JSON.stringify(body),
    });
    if (r.status === 401 || r.status === 403) { flash('⚠ admin login required'); return null; }
    return r.json().catch(() => null);
  };

  // EXIT — us bot×symbol ke SAB open legs flat (/squareoff/one per tradeId).
  const exitRow = async (c: Cell) => {
    if (!window.confirm(`${c.symbol} (${c.botName}) ke ${c.openLegs.length} open leg(s), qty ${c.openQty} — FLAT karein?`)) return;
    setBusy(`${c.bot}|${c.symbol}`);
    let ok = 0, fail = 0;
    for (const leg of c.openLegs) {
      const d = await post('/squareoff/one', { tradeId: leg.id });
      if (d?.status === 'success') ok++; else fail++;
    }
    setBusy('');
    flash(fail === 0 ? `✅ ${c.symbol} flat (${ok} leg)` : `⚠ ${c.symbol}: ${ok} flat, ${fail} FAIL — dobara try karo`);
    void load();
  };

  // EXIT one TIMEFRAME's legs only (God 2026-08-24). Genuinely per-TF: square-off
  // is keyed by tradeId, so this closes exactly the legs that TF opened and leaves
  // the symbol's other timeframes running. Same endpoint, narrower leg set.
  const exitTf = async (c: Cell, tf: string, sl: TfSlice) => {
    if (!window.confirm(`${c.symbol} ${tf} (${c.botName}) — ${sl.openLegs.length} leg, qty ${sl.openQty} FLAT karein?\n\nBaaki timeframes chalte rahenge.`)) return;
    setBusy(`${c.bot}|${c.symbol}|${tf}`);
    let ok = 0, fail = 0;
    for (const leg of sl.openLegs) {
      const d = await post('/squareoff/one', { tradeId: leg.id });
      if (d?.status === 'success') ok++; else fail++;
    }
    setBusy('');
    flash(fail === 0 ? `✅ ${c.symbol} ${tf} flat (${ok} leg)` : `⚠ ${c.symbol} ${tf}: ${ok} flat, ${fail} FAIL — dobara try karo`);
    void load();
  };

  // STOP — L0 symbol-kill (sab bots ki us symbol ki NAYI entries block; exits kabhi nahi).
  const stopRow = async (c: Cell, withExit: boolean) => {
    const reason = window.prompt(`${c.symbol} ko STOP (kill) karna hai? Reason:`, withExit ? 'error — exit+stop' : 'manual risk-off');
    if (reason === null) return;
    setBusy(`${c.bot}|${c.symbol}`);
    const d = await post('/risk/symbol-kill', { symbol: c.symbol, reason, squareOff: withExit });
    setBusy('');
    flash(d?.status === 'success' ? `🔴 ${c.symbol} STOPPED${withExit ? ' + FLAT' : ''}` : `✗ ${d?.message || 'failed'}`);
    void load();
  };

  const resumeRow = async (c: Cell) => {
    const d = await post('/risk/symbol-kill/reset', { symbol: c.symbol });
    flash(d?.status === 'success' ? `🟢 ${c.symbol} RESUMED` : `✗ ${d?.message || 'failed'}`);
    void load();
  };

  const statusOf = (c: Cell): string => {
    if (kills.includes(c.symbol)) return 'KILLED';
    if (c.openQty > 0) return 'RUNNING';
    if (c.lastClosed === 'LOSS') return 'SL HIT';
    if (c.lastClosed === 'WIN') return 'BOOKED';
    return 'IDLE';
  };

  // bot-grouped render + fleet footer (Gate 4 cap 5000)
  const byBot = new Map<string, Cell[]>();
  cells.forEach(c => { const a = byBot.get(c.bot) || []; a.push(c); byBot.set(c.bot, a); });
  const fleetPnl = cells.reduce((s, c) => s + c.dayRealized, 0);
  const fleetRun = cells.reduce((s, c) => s + runPnlOf(c).pnl, 0);
  const fleetHasRun = cells.some(c => runPnlOf(c).has);
  const FLEET_CAP = 5000;

  const capBar = (pnl: number, cap: number) => {
    const used = pnl < 0 ? Math.min(1, -pnl / cap) : 0;
    const color = used > 0.9 ? '#ef4444' : used > 0.6 ? '#f59e0b' : '#10b981';
    return (
      <span style={{ display: 'inline-block', width: 60, height: 5, background: '#131a2e', borderRadius: 3, verticalAlign: 'middle', marginLeft: 6 }}>
        <span style={{ display: 'block', width: `${used * 100}%`, height: '100%', background: color, borderRadius: 3 }} />
      </span>
    );
  };

  // status counts (strip summary chips)
  const counts = { running: 0, sl: 0, killed: 0 };
  cells.forEach(c => {
    const st = statusOf(c);
    if (st === 'RUNNING') counts.running++;
    else if (st === 'SL HIT') counts.sl++;
    else if (st === 'KILLED') counts.killed++;
  });

  // ── FULL-DETAIL TABLE (modal ke andar render hota hai) ──
  // A symbol's TIMEFRAME rows, rendered directly under that symbol (God
  // 2026-08-26). These used to be emitted as one block AFTER every symbol row in
  // the bot, so "BANKEX 15s" appeared underneath NIFTY_50 and read as NIFTY_50's
  // child — the hierarchy the split exists to show was the one thing it hid.
  //
  // A symbol with a single timeframe returns nothing: one child under one parent
  // is not a split, it is the same row written twice.
  //
  // EXIT here is REAL — it squares off exactly this TF's tradeIds. STOP is
  // deliberately NOT offered per TF: /risk/symbol-kill is symbol-scoped, so a
  // per-TF STOP button would kill the whole symbol while claiming otherwise. It
  // stays on the symbol row.
  const tfRowsOf = (c: Cell): React.ReactNode[] => {
    if (!openSym.has(`${c.bot}|${c.symbol}`)) return [];   // closed until clicked
    const tfs = Object.entries(c.byTf).sort((a, b) => tfRank(a[0]) - tfRank(b[0]));
    if (tfs.length <= 1) return [];   // nothing to split
    return tfs.flatMap(([tf, sl]) => {
      const r = runPnlOfLegs(sl.openLegs);
      const tfBusy = busy === `${c.bot}|${c.symbol}|${tf}`;
      const noTf = tf === '(no TF)';
      const tfKey = `${c.bot}|${c.symbol}|${tf}`;
      const tfOpen = openTf.has(tfKey);
      const cons = Object.values(sl.contracts)
        .sort((a, b) => (b.openQty - a.openQty) || (b.trades - a.trades));
      // A leg that touched more than one contract today is the
      // shape the strike-drift bug produces, so it is called out
      // on the collapsed row rather than hidden one click away.
      const badMix = cons.reduce((n, x) => n + x.mismatched, 0);
      return [
        <tr key={tfKey} style={{ background: 'rgba(56,189,248,.02)' }}>
          <td style={{ ...S.td, paddingLeft: 22, color: '#64748b', fontSize: 9.5, cursor: cons.length ? 'pointer' : 'default' }}
              onClick={() => { if (!cons.length) return; const n = new Set(openTf); n.has(tfKey) ? n.delete(tfKey) : n.add(tfKey); setOpenTf(n); }}
              title={cons.length ? `${cons.length} contract${cons.length === 1 ? '' : 's'} traded — click for buy/sell per contract` : undefined}>
            <span style={{ display: 'inline-block', width: 9, color: cons.length ? '#64748b' : 'transparent',
                           transform: tfOpen ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }}>▶</span>
            {c.symbol}{' '}
            <span style={{ fontWeight: 900, fontFamily: 'monospace', padding: '0 5px', borderRadius: 3,
                           background: noTf ? 'rgba(239,68,68,.15)' : 'rgba(56,189,248,.12)',
                           color: noTf ? '#f87171' : '#38bdf8' }}>{tf}</span>
            {cons.length > 1 && (
              <span title="more than one contract traded on this leg today"
                    style={{ marginLeft: 5, fontSize: 8, fontWeight: 800, color: '#fbbf24' }}>
                {cons.length} contracts
              </span>
            )}
            {badMix > 0 && (
              <span title="exit named a DIFFERENT contract than the entry — the ledger recorded the mismatch"
                    style={{ marginLeft: 5, fontSize: 8, fontWeight: 900, color: '#f87171',
                             border: '1px solid rgba(248,113,113,.45)', borderRadius: 3, padding: '0 4px' }}>
                ⚠ {badMix} MISMATCH
              </span>
            )}
          </td>
          <td style={{ ...S.td, fontSize: 9.5, color: '#64748b' }}>{sl.openQty || '—'}</td>
          <td style={{ ...S.td, fontSize: 9, color: '#64748b' }}>
            {sl.openLegs.length ? sl.openLegs.map(legLine) : '—'}
          </td>
          <td style={{ ...S.td, fontSize: 9.5, fontFamily: 'monospace', fontWeight: 800,
                       color: !sl.openQty ? '#475569' : !r.has ? '#64748b' : r.pnl >= 0 ? '#34d399' : '#f87171' }}>
            {!sl.openQty ? '—' : r.has ? fmt(r.pnl) : '…'}
          </td>
          <td style={{ ...S.td, fontSize: 9.5, fontWeight: 800, color: sl.dayRealized >= 0 ? '#34d399' : '#f87171' }}>{fmt(sl.dayRealized)}{usageLine(sliceUsage(sl), sl.dayRealized)}</td>
          {/* The three numbers God asked for on every TF leg:
              how many trades it did today, and the quantity-
              weighted average price it bought and sold at. */}
          <td style={{ ...S.td, fontSize: 9, color: '#64748b', whiteSpace: 'nowrap' }}>
            <span style={{ fontWeight: 800, color: sl.trades ? '#cbd5e1' : '#475569' }}>{sl.trades}</span>
            <span style={{ color: '#334155' }}> trd · </span>
            <span title="today's quantity-weighted average BUY price">
              B <span style={{ fontFamily: 'monospace', color: sl.buyQty ? '#34d399' : '#475569' }}>{pxFmt(wavg(sl.buySum, sl.buyQty))}</span>
            </span>
            <span style={{ color: '#334155' }}> · </span>
            <span title="today's quantity-weighted average SELL price">
              S <span style={{ fontFamily: 'monospace', color: sl.sellQty ? '#f87171' : '#475569' }}>{pxFmt(wavg(sl.sellSum, sl.sellQty))}</span>
            </span>
          </td>
          <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
            {!noTf && (
              <button onClick={() => setChartTarget(chartForCell(c, tf))}
                      title={sl.openLegs[0] ? `live chart of the open contract at ${tf}` : `live chart of ${c.symbol} at ${tf}`}
                      style={S.btn('#a5b4fc', 'rgba(99,102,241,.12)', 'rgba(99,102,241,.45)')}>📈 CHART</button>
            )}
            {tfBusy ? <span style={{ fontSize: 9, color: '#fbbf24' }}>⏳…</span>
              : sl.openQty > 0
                ? <button onClick={() => void exitTf(c, tf, sl)} style={S.btn('#fbbf24', 'rgba(245,158,11,.08)', 'rgba(245,158,11,.35)')}>EXIT {tf}</button>
                : null}
          </td>
        </tr>,
        // ── CONTRACT LEVEL ────────────────────────────────────
        // Which option this leg actually traded. Buy and sell sit
        // on ONE line per contract, so "bought 57900 PE, sold
        // 58000 PE" shows up as two rows each missing a side —
        // the thing that is invisible in a single day-P&L figure.
        ...(tfOpen && cons.length ? [(
          <tr key={`${tfKey}|contracts`} style={{ background: 'rgba(15,23,42,.55)' }}>
            <td colSpan={7} style={{ ...S.td, padding: '6px 8px 8px 40px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9 }}>
                <thead>
                  <tr>
                    {['CONTRACT', 'TRD', 'AVG BUY', 'AVG SELL', 'OPEN QTY', 'REALIZED'].map((h, i) => (
                      <th key={h} style={{ ...S.th, padding: '2px 6px', textAlign: i ? 'right' : 'left' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cons.map(x => {
                    const oneSided = (x.buyQty > 0) !== (x.sellQty > 0) && x.openQty === 0;
                    return (
                      <React.Fragment key={x.label}>
                      <tr>
                        <td style={{ ...S.td, padding: '2px 6px', fontFamily: 'monospace', color: x.mismatched ? '#f87171' : '#cbd5e1' }}>
                          {x.label}
                          {x.openQty > 0 && <span style={{ marginLeft: 5, fontSize: 8, fontWeight: 800, color: '#34d399' }}>OPEN</span>}
                          {oneSided && (
                            <span title="this contract has a fill on only ONE side and nothing left open — its other side landed somewhere else"
                                  style={{ marginLeft: 5, fontSize: 8, fontWeight: 800, color: '#fbbf24' }}>
                              one-sided
                            </span>
                          )}
                        </td>
                        <td style={{ ...S.td, padding: '2px 6px', textAlign: 'right', color: '#94a3b8' }}>{x.trades}</td>
                        <td style={{ ...S.td, padding: '2px 6px', textAlign: 'right', fontFamily: 'monospace', color: x.buyQty ? '#34d399' : '#475569' }}>
                          {pxFmt(wavg(x.buySum, x.buyQty))}
                        </td>
                        <td style={{ ...S.td, padding: '2px 6px', textAlign: 'right', fontFamily: 'monospace', color: x.sellQty ? '#f87171' : '#475569' }}>
                          {pxFmt(wavg(x.sellSum, x.sellQty))}
                        </td>
                        <td style={{ ...S.td, padding: '2px 6px', textAlign: 'right', color: x.openQty ? '#e2e8f0' : '#475569' }}>{x.openQty || '—'}</td>
                        <td style={{ ...S.td, padding: '2px 6px', textAlign: 'right', fontWeight: 800, color: x.realized >= 0 ? '#34d399' : '#f87171' }}>{fmt(x.realized)}</td>
                      </tr>
                      {/* EVERY TRADE, TIME-STAMPED (God 2026-09-29: "buy and sell ka entry time and exit time stamp
                          karay ki pine point error chak ho sakay"). IST wall times straight from the ledger row; spot
                          in -> out is the underlying the Pine judges; the chart button opens the INDEX at this TF
                          centred on this trade's entry, with the Pine's own signals beside it. */}
                      <tr>
                        <td colSpan={6} style={{ padding: '0 6px 6px 18px', borderBottom: '1px solid #131a2e' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 8.5 }}>
                            <thead>
                              <tr>
                                {['#', 'ENTRY', 'BUY @', 'SPOT IN', 'EXIT', 'SELL @', 'SPOT OUT', 'HOLD', 'QTY', 'NET', 'EXIT REASON', ''].map((h, i) => (
                                  <th key={h + i} style={{ ...S.th, padding: '1px 5px', fontSize: 7.5, textAlign: i >= 2 && i <= 9 ? 'right' : 'left' }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {[...x.rows].sort((p, q) => String(p.time || '').localeCompare(String(q.time || ''))).map((r: any, i: number) => {
                                const closed = ['WIN', 'LOSS', 'BREAKEVEN'].includes(String(r.status));
                                const net = Number(r.netPnl ?? r.pnl) || 0;
                                const hold = (() => {
                                  const toS = (h?: string) => { const m = /^(\d{1,2}):(\d{2}):?(\d{2})?$/.exec(String(h || '')); return m ? (+m[1]) * 3600 + (+m[2]) * 60 + (+(m[3] || 0)) : NaN; };
                                  const d = toS(r.exitTime) - toS(r.time);
                                  return Number.isFinite(d) && d >= 0 ? (d >= 60 ? `${Math.floor(d / 60)}m${String(d % 60).padStart(2, '0')}s` : `${d}s`) : '—';
                                })();
                                const px = (v: any) => (Number(v) > 0 ? Number(v).toFixed(2) : '—');
                                const td = (v: React.ReactNode, right = true, color = '#cbd5e1', extra: React.CSSProperties = {}) => (
                                  <td style={{ padding: '1px 5px', borderBottom: '1px solid #0f172a', textAlign: right ? 'right' : 'left',
                                               fontFamily: 'monospace', color, whiteSpace: 'nowrap', ...extra }}>{v}</td>
                                );
                                return (
                                  <tr key={r.id || i} title={`${r.id || ''} · pine ${r.pineVersion || '—'}`}>
                                    {td(i + 1, false, '#475569')}
                                    {td(r.time || '—', false, '#34d399', { fontWeight: 800 })}
                                    {td(px(r.entryPrice))}
                                    {td(px(r.spotEntry), true, '#94a3b8')}
                                    {td(closed ? (r.exitTime || '—') : 'OPEN', false, closed ? '#f87171' : '#fbbf24', { fontWeight: 800 })}
                                    {td(closed ? px(r.exitPrice) : '—')}
                                    {td(closed ? px(r.spotExit) : '—', true, '#94a3b8')}
                                    {td(closed ? hold : '—', true, '#64748b')}
                                    {td(r.qty ?? '—', true, '#94a3b8')}
                                    {td(closed ? fmt(net) : '—', true, !closed ? '#475569' : net >= 0 ? '#34d399' : '#f87171', { fontWeight: 800 })}
                                    {td(String(r.exitReason || (closed ? '' : 'open')).slice(0, 38), false, '#64748b', { fontFamily: 'inherit' })}
                                    <td style={{ padding: '1px 5px', borderBottom: '1px solid #0f172a' }}>
                                      <button onClick={() => setChartTarget(chartTargetForPinpoint(c.bot, c.symbol, tf, r))}
                                        title="open the index at this TF centred on this trade - fills + Pine signals"
                                        style={{ ...S.btn('#a5b4fc', 'rgba(99,102,241,.12)', 'rgba(99,102,241,.45)'), fontSize: 7.5, padding: '1px 6px' }}>📈</button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </td>
          </tr>
        )] : []),
      ];
    });
  };

  // PRINT / PDF (God 2026-10-06 ~19:13: "print ka option aur pdf file ka option, full detail").
  // One printable document: fleet summary, every bot, every symbol x TF leg with fund / win % /
  // return %, and every trade under it. PDF = the browser's "Save as PDF" in the same dialog.
  const printReport = (asPdf: boolean) => {
    const w = window.open('', '_blank');
    if (!w) { setMsg('Popup block hua - browser me popups allow karein'); return; }
    const esc = (x: any) => String(x ?? '').replace(/[&<>]/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' } as Record<string, string>)[ch]);
    const col = (n: number) => (n > 0 ? '#16a34a' : n < 0 ? '#dc2626' : '#475569');
    const pc = (n: number | null) => (n === null || !Number.isFinite(n) ? '—' : `${n > 0 ? '+' : ''}${n.toFixed(2)}%`);
    const wr = (u: U) => (u.closed ? `${(100 * u.wins / u.closed).toFixed(1)}%` : '—');
    const ret = (pnl: number, u: U) => (u.fund ? 100 * pnl / u.fund : null);
    const now = new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false });
    const all = sumUsage(cells.map(cellUsage));
    let body = '';
    for (const [bot, rows] of Array.from(byBot.entries())) {
      const bp = rows.reduce((a, c) => a + c.dayRealized, 0);
      const bu = sumUsage(rows.map(cellUsage));
      const cap = botCaps[bot]?.maxLoss || 5000;
      body += `<div class="bot"><span>${esc(botCaps[bot]?.name || rows[0].botName)}</span>
        <span>Day <b style="color:${col(bp)}">${esc(fmt(bp))}</b> / cap ₹${cap.toLocaleString('en-IN')} · Fund ${esc(inrShort(bu.fund))} · Win ${wr(bu)} · Return <b style="color:${col(bp)}">${pc(ret(bp, bu))}</b></span></div>
        <table><thead><tr><th>Symbol</th><th>TF</th><th>Trades</th><th>Open qty</th><th>Day realized</th><th>Fund used</th><th>Win %</th><th>Return %</th><th>Status</th></tr></thead><tbody>`;
      for (const c of [...rows].sort((a, b) => a.symbol.localeCompare(b.symbol))) {
        const tfs = Object.keys(c.byTf).sort((a, b) => tfRank(a) - tfRank(b));
        const cu = cellUsage(c);
        body += `<tr class="sym"><td>${esc(c.symbol)}</td><td>${tfs.length} TF</td><td>${tfs.reduce((a, t) => a + c.byTf[t].trades, 0)}</td><td>${c.openQty || '—'}</td>
          <td style="color:${col(c.dayRealized)}">${esc(fmt(c.dayRealized))}</td><td>${esc(inrShort(cu.fund))}</td><td>${wr(cu)}</td>
          <td style="color:${col(c.dayRealized)}">${pc(ret(c.dayRealized, cu))}</td><td>${esc(statusOf(c))}</td></tr>`;
        for (const tf of tfs) {
          const sl = c.byTf[tf];
          const su = sliceUsage(sl);
          body += `<tr class="tf"><td>↳</td><td>${esc(tf)}</td><td>${sl.trades}</td><td>${sl.openQty || '—'}</td>
            <td style="color:${col(sl.dayRealized)}">${esc(fmt(sl.dayRealized))}</td><td>${esc(inrShort(su.fund))}</td><td>${wr(su)}</td>
            <td style="color:${col(sl.dayRealized)}">${pc(ret(sl.dayRealized, su))}</td><td></td></tr>`;
          const trs = Object.values(sl.contracts).flatMap(cs => cs.rows.map(r => ({ r, label: cs.label })));
          if (trs.length) {
            body += `<tr><td colspan="9" class="trd"><table class="inner"><thead><tr><th>#</th><th>Contract</th><th>Entry</th><th>Entry ₹</th><th>Exit</th><th>Exit ₹</th><th>Qty</th><th>Capital</th><th>Status</th><th>Net (all-incl)</th><th>Exit reason</th></tr></thead><tbody>${
              trs.map(({ r, label }, i) => {
                const closed = ['WIN', 'LOSS', 'BREAKEVEN'].includes(r.status);
                const net = Number(r.netPnl ?? r.pnl) || 0;
                return `<tr><td>${i + 1}</td><td>${esc(label)}</td><td>${esc(r.time || r.entryTime || '—')}</td><td>${esc(r.entryPrice ?? '—')}</td>
                  <td>${esc(closed ? (r.exitTime || '—') : 'OPEN')}</td><td>${esc(closed ? (r.exitPrice ?? '—') : '—')}</td><td>${esc(r.qty ?? '—')}</td>
                  <td>${Number(r.capitalUsed) ? esc(inrShort(Number(r.capitalUsed))) : '—'}</td><td>${esc(r.status || '')}</td>
                  <td style="color:${col(net)}">${closed ? esc(fmt(net)) : '—'}</td><td>${esc(String(r.exitReason || '').slice(0, 60))}</td></tr>`;
              }).join('')}</tbody></table></td></tr>`;
          }
        }
      }
      body += '</tbody></table>';
    }
    const laneTxt = LANE_MODE === 'LIVE' ? 'LIVE · KALI (real money)' : 'PAPER · DUMABATI';
    w.document.write(`<html><head><title>Granular Position Manager — ${esc(laneTxt)} — ${esc(now)}</title><style>
      *{box-sizing:border-box} body{font-family:'Segoe UI',Arial,sans-serif;padding:22px;color:#1e293b}
      h1{font-size:18px;margin:0 0 4px} .meta{color:#64748b;font-size:11px;margin-bottom:12px}
      .kpis{display:flex;gap:10px;flex-wrap:wrap;margin-bottom:14px} .k{background:#0f172a;color:#fff;border-radius:6px;padding:8px 12px;font-size:12px;font-weight:700}
      .bot{display:flex;justify-content:space-between;gap:10px;flex-wrap:wrap;background:#334155;color:#fff;padding:9px 12px;border-radius:5px;margin:18px 0 6px;font-weight:800;font-size:13px}
      .bot b{background:#fff;padding:1px 6px;border-radius:4px}
      table{width:100%;border-collapse:collapse;font-size:11px} th{background:#eef2f7;text-align:left;padding:5px 6px;font-size:10px;color:#475569}
      td{padding:4px 6px;border-bottom:1px solid #e2e8f0} tr.sym td{font-weight:800;background:#f8fafc} tr.tf td{color:#334155}
      td.trd{padding:0 0 6px 24px;border:0} table.inner{font-size:9.5px} table.inner th{background:#f1f5f9}
      .pdfhint{background:#fef3c7;border:1px solid #f59e0b;padding:8px 12px;border-radius:6px;font-size:12px;margin-bottom:12px}
      @media print{.pdfhint{display:none} .bot,tr.sym{break-inside:avoid}}
    </style></head><body>
      ${asPdf ? '<div class="pdfhint">PDF ke liye: print dialog me <b>Destination → "Save as PDF"</b> chuniye, phir Save.</div>' : ''}
      <h1>Jaiswal Capital — Granular Position Manager</h1>
      <div class="meta">${esc(laneTxt)} · ${esc(now)} IST · P&amp;L = ALL-INCLUSIVE (charges ke baad) · Fund used = sabse bada ek-trade capital (har TF leg ka jod) · Return % = realized ÷ fund</div>
      <div class="kpis"><span class="k">Fleet day P&amp;L ${esc(fmt(fleetPnl))} / cap ₹${FLEET_CAP.toLocaleString('en-IN')}</span>
        ${fleetHasRun ? `<span class="k">Running ${esc(fmt(fleetRun))}</span>` : ''}
        <span class="k">Fund used ${esc(inrShort(all.fund))}</span><span class="k">Win ${wr(all)}</span>
        <span class="k">Return ${pc(ret(fleetPnl, all))}</span>
        <span class="k">${counts.running} running · ${counts.sl} SL hit · ${counts.killed} killed</span></div>
      ${body || '<div>Aaj koi trade / position nahi.</div>'}
    </body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 350);
  };

  const fullTable = (
    <>
      <div style={{ overflowX: 'auto' }}>
        {cells.length === 0 && (
          <div style={{ padding: 20, fontSize: 11, color: '#64748b', textAlign: 'center' }}>
            aaj koi trade/position nahi — pehli entry ke saath rows yahan aayengi
          </div>
        )}
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>SYMBOL</th><th style={S.th}>OPEN QTY</th>
              <th style={S.th} title="asset · avg entry → live LTP (source) · SL (p = premium, u = underlying) · TGT (underlying) · M2M · 📈 chart">OPEN LEGS · LIVE</th>
              <th style={{ ...S.th, color: '#38bdf8' }}>RUNNING P&L</th>
              <th style={S.th}>DAY REALIZED</th><th style={S.th}>STATUS</th><th style={S.th}>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {Array.from(byBot.entries()).map(([bot, rows]) => {
              const botPnl = rows.reduce((s, c) => s + c.dayRealized, 0);
              const cap = botCaps[bot]?.maxLoss || 5000;
              return (
                <React.Fragment key={bot}>
                  <tr>
                    <td colSpan={7} style={{ ...S.td, background: '#0e1526', fontWeight: 900, fontSize: 9, color: '#a5b4fc' }}>
                      🤖 {botCaps[bot]?.name || rows[0].botName} · day {fmt(botPnl)} / cap ₹{cap.toLocaleString('en-IN')}{capBar(botPnl, cap)}
                      {(() => { const u = sumUsage(rows.map(cellUsage)); return (
                        <span style={{ marginLeft: 10, fontWeight: 800 }} title="Fund used = har symbol × TF leg ke sabse bade ek-trade capital ka jod · Return % = day realized (all-incl) ÷ fund">
                          <span style={{ color: '#a5b4fc' }}>· fund {inrShort(u.fund)}</span>
                          <span style={{ color: '#fbbf24', marginLeft: 6 }}>win {u.closed ? `${(100 * u.wins / u.closed).toFixed(1)}%` : '—'}</span>
                          <span style={{ color: pctCol(u.fund ? 100 * botPnl / u.fund : null), marginLeft: 6 }}>return {pctTxt(u.fund ? 100 * botPnl / u.fund : null)}</span>
                        </span>); })()}
                      {(() => { const br = rows.reduce((s, c) => s + runPnlOf(c).pnl, 0); const bh = rows.some(c => runPnlOf(c).has); return bh ? <span style={{ marginLeft: 10, color: br >= 0 ? '#34d399' : '#f87171' }}>· running {fmt(br)}</span> : null; })()}
                    </td>
                  </tr>
                  {rows.sort((a, b) => b.openQty - a.openQty || a.symbol.localeCompare(b.symbol)).flatMap(c => {
                    const st = statusOf(c);
                    const sm = STATUS_META[st];
                    const rowBusy = busy === `${c.bot}|${c.symbol}`;
                    const symKey = `${c.bot}|${c.symbol}`;
                    const symOpen = openSym.has(symKey);
                    const tfCount = Object.keys(c.byTf).length;
                    return [
                      <tr key={`${c.bot}|${c.symbol}`}>
                        {/* The symbol cell is the accordion handle. Only the
                            CELL toggles, never the whole row — the row carries
                            EXIT / STOP / EXIT+STOP, and a click that fires a
                            trade action must never be one a reader makes by
                            aiming for a caret. */}
                        <td style={{ ...S.td, fontWeight: 800, cursor: tfCount > 1 ? 'pointer' : 'default', userSelect: 'none' }}
                            onClick={() => { if (tfCount <= 1) return; const n = new Set(openSym); n.has(symKey) ? n.delete(symKey) : n.add(symKey); setOpenSym(n); }}
                            title={tfCount > 1 ? `${tfCount} timeframes — click to open` : undefined}>
                          <span style={{ display: 'inline-block', width: 10, color: tfCount > 1 ? '#64748b' : 'transparent',
                                         transform: symOpen ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }}>▶</span>
                          {c.symbol}
                          {tfCount > 1 && (
                            <span style={{ marginLeft: 5, fontSize: 8, fontWeight: 800, color: symOpen ? '#38bdf8' : '#475569' }}>
                              {tfCount} TF
                            </span>
                          )}
                          {c.voidedCount > 0 && <span title="voided fake-price trade(s) — pnl excluded" style={{ color: '#f59e0b', marginLeft: 4 }}>⚑{c.voidedCount}</span>}
                        </td>
                        <td style={S.td}>{c.openQty || '—'}</td>
                        <td style={{ ...S.td, fontSize: 9, color: '#94a3b8' }}>
                          {c.openLegs.length ? c.openLegs.map(legLine) : '—'}
                        </td>
                        {(() => { const r = runPnlOf(c); return (
                          <td style={{ ...S.td, fontWeight: 800, fontFamily: 'monospace', color: !c.openQty ? '#475569' : !r.has ? '#64748b' : r.pnl >= 0 ? '#34d399' : '#f87171' }}
                              title="Running M2M of open legs = Σ (live contract LTP − entry) × openQty × dir — pnlMath.runningM2M on the ws/chain tick, bridge figure as fallback">
                            {!c.openQty ? '—' : r.has ? fmt(r.pnl) : '…'}
                          </td>
                        ); })()}
                        <td style={{ ...S.td, fontWeight: 800, color: c.dayRealized >= 0 ? '#34d399' : '#f87171' }}>{fmt(c.dayRealized)}{usageLine(cellUsage(c), c.dayRealized)}</td>
                        <td style={{ ...S.td, fontWeight: 800, color: sm.color }}>{sm.icon} {st}</td>
                        <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                          <button onClick={() => setChartTarget(chartForCell(c))}
                                  title={c.openLegs[0] ? 'live chart of the open contract at its own timeframe' : `live chart of ${c.symbol}`}
                                  style={S.btn('#a5b4fc', 'rgba(99,102,241,.12)', 'rgba(99,102,241,.45)')}>📈 CHART</button>
                          {rowBusy ? <span style={{ fontSize: 9, color: '#fbbf24' }}>⏳ working…</span> : <>
                            {c.openQty > 0 && (
                              <button onClick={() => void exitRow(c)} style={S.btn('#fbbf24', 'rgba(245,158,11,.1)', 'rgba(245,158,11,.45)')}>EXIT</button>
                            )}
                            {st === 'KILLED'
                              ? <button onClick={() => void resumeRow(c)} style={S.btn('#34d399', 'rgba(16,185,129,.15)', 'rgba(16,185,129,.5)')}>RESUME</button>
                              : <>
                                  <button onClick={() => void stopRow(c, false)} style={S.btn('#f87171', 'rgba(239,68,68,.1)', 'rgba(239,68,68,.45)')}>STOP</button>
                                  {c.openQty > 0 && (
                                    <button onClick={() => void stopRow(c, true)} style={S.btn('#f87171', 'rgba(239,68,68,.2)', 'rgba(239,68,68,.6)')}>EXIT+STOP</button>
                                  )}
                                </>}
                          </>}
                        </td>
                      </tr>,
                      // This symbol's own timeframe rows, immediately beneath it.
                      ...tfRowsOf(c),
                    ];
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );

  // ── RENDER: panel-variant (tab ke andar inline) ya slim strip; dono se
  //    FULL SCREEN modal khulta hai ──
  const summaryChips = (
    <>
      <span style={{ fontSize: 9, fontWeight: 800, color: fleetPnl >= 0 ? '#34d399' : '#f87171' }}>
        FLEET {fmt(fleetPnl)} / ₹{FLEET_CAP.toLocaleString('en-IN')} (GATE 4){capBar(fleetPnl, FLEET_CAP)}
      </span>
      {fleetHasRun && (
        <span style={{ fontSize: 9, fontWeight: 800, color: fleetRun >= 0 ? '#34d399' : '#f87171' }} title="Live unrealized P&L across all open positions">
          ⚡ RUNNING {fmt(fleetRun)}
        </span>
      )}
      <span style={{ fontSize: 9, color: '#94a3b8' }}>
        🟢 {counts.running} running · 🟡 {counts.sl} SL hit · 🔴 {counts.killed} killed
      </span>
      {msg && <span style={{ fontSize: 9, fontWeight: 700, color: '#a5b4fc' }}>{msg}</span>}
    </>
  );

  return (
    <>
      {variant === 'panel' ? (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '6px 8px' }}>
            {summaryChips}
            <span style={{ flex: 1 }} />
            <button onClick={() => printReport(false)} style={S.btn('#e2e8f0', 'rgba(148,163,184,.12)', 'rgba(148,163,184,.45)')}>🖨 PRINT</button>
            <button onClick={() => printReport(true)} style={S.btn('#fca5a5', 'rgba(239,68,68,.1)', 'rgba(239,68,68,.45)')}>⬇ PDF</button>
            <button onClick={() => { setOpen(true); setFs(true); }}
              style={S.btn('#a5b4fc', 'rgba(99,102,241,.12)', 'rgba(99,102,241,.45)')}>⛶ FULL SCREEN</button>
          </div>
          {fullTable}
        </div>
      ) : (
        <div
          onClick={() => setOpen(true)}
          title="Click → full detail"
          style={{ ...S.wrap, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', cursor: 'pointer' }}
        >
          <span style={S.head}>⚡ POSITION MANAGER</span>
          {summaryChips}
          <button
            onClick={(e) => { e.stopPropagation(); setOpen(true); }}
            style={S.btn('#a5b4fc', 'rgba(99,102,241,.12)', 'rgba(99,102,241,.45)')}
          >▦ FULL VIEW</button>
        </div>
      )}

      {open && (
        <div
          onClick={() => { setOpen(false); setFs(false); }}
          style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(3,6,15,.78)',
                   display: 'flex', alignItems: 'center', justifyContent: 'center',
                   padding: fs ? 0 : 24 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#0b0f1a', border: '1px solid #1a1f3a',
                     borderRadius: fs ? 0 : 12, boxShadow: '0 24px 80px rgba(0,0,0,.6)',
                     width: fs ? '100vw' : 'min(1240px, 96vw)',
                     height: fs ? '100dvh' : 'min(78vh, 860px)',
                     display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
                          borderBottom: '1px solid #1a1f3a', background: '#0e1526' }}>
              <span style={{ fontSize: 12, fontWeight: 900, color: '#e2e8f0', letterSpacing: 1 }}>
                ⚡ GRANULAR POSITION MANAGER
              </span>
              <span style={{ fontSize: 10, fontWeight: 800, color: fleetPnl >= 0 ? '#34d399' : '#f87171' }}>
                FLEET DAY P&L {fmt(fleetPnl)} / cap ₹{FLEET_CAP.toLocaleString('en-IN')} (GATE 4){capBar(fleetPnl, FLEET_CAP)}
              </span>
              {msg && <span style={{ fontSize: 10, fontWeight: 700, color: '#a5b4fc' }}>{msg}</span>}
              <span style={{ flex: 1 }} />
              <button onClick={() => printReport(false)} title="Poori report print karein"
                style={S.btn('#e2e8f0', 'rgba(148,163,184,.12)', 'rgba(148,163,184,.45)')}>🖨 PRINT</button>
              <button onClick={() => printReport(true)} title="PDF file: print dialog me Save as PDF"
                style={S.btn('#fca5a5', 'rgba(239,68,68,.1)', 'rgba(239,68,68,.45)')}>⬇ PDF</button>
              <button onClick={() => setFs(f => !f)}
                style={S.btn('#a5b4fc', 'rgba(99,102,241,.12)', 'rgba(99,102,241,.45)')}>
                {fs ? '🗗 EXIT FULL SCREEN' : '⛶ FULL SCREEN'}
              </button>
              <button onClick={() => { setOpen(false); setFs(false); }}
                style={S.btn('#f87171', 'rgba(239,68,68,.1)', 'rgba(239,68,68,.45)')}>✕ CLOSE</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '4px 12px 12px' }}>
              {fullTable}
            </div>
          </div>
        </div>
      )}

      {/* CHART quick action — sits above the 9000 modal, view-only pane. */}
      {chartTarget && (
        <Suspense fallback={null}>
          <ContractChartDrawer target={chartTarget} lane={lane} onClose={() => setChartTarget(null)} zIndex={9600} />
        </Suspense>
      )}
    </>
  );
};

export default GranularPositionManager;
